import React, { useEffect, useState } from 'react';
import { Trash2, Upload, Pencil } from 'lucide-react';
import { adminApi } from '../services/adminApi';
import { Modal } from './Modal';
import { COLORS } from '../../theme/colors';

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function extractError(err) {
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') return detail.message || JSON.stringify(detail);
  return err.message || 'Unknown error';
}

function TemplateForm({ workspaceId, mode, template, onDone }) {
  const isEdit = mode === 'edit';
  const [name, setName] = useState(isEdit ? template.name : '');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (!isEdit && !file) return;
    setBusy(true);
    setError(null);
    try {
      if (isEdit) {
        await adminApi.updateReceiptTemplate(workspaceId, template.id, {
          name: name.trim() !== template.name ? name.trim() : undefined,
          file: file || undefined,
        });
      } else {
        await adminApi.createReceiptTemplate(workspaceId, file, name.trim());
      }
      onDone();
    } catch (err) {
      setError(extractError(err));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit}>
      <div style={{ marginBottom: 12 }}>
        <label className="admin-label">Name</label>
        <input
          type="text"
          className="admin-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={120}
          required
          autoFocus
        />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label className="admin-label">{isEdit ? 'Replace .docx file (optional)' : '.docx template file'}</label>
        <input
          type="file"
          accept=".docx"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          required={!isEdit}
        />
      </div>
      {error && (
        <div style={{
          padding: '8px 12px',
          background: 'var(--danger-bg)',
          border: `1px solid ${COLORS.dangerBorder}`,
          borderRadius: 6,
          fontSize: 13,
          color: 'var(--danger-text)',
          marginBottom: 12,
        }}>
          {error}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="submit" className="admin-btn admin-btn-primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

export function ReceiptTemplatesPanel({ workspace, isOpen, onClose }) {
  const [templates, setTemplates] = useState(null);
  const [view, setView] = useState('list'); // 'list' | 'create' | 'edit'
  const [editing, setEditing] = useState(null);

  const reload = async () => {
    if (!workspace) return;
    const list = await adminApi.getReceiptTemplates(workspace.id);
    setTemplates(list);
  };

  useEffect(() => {
    if (!isOpen) return;
    setView('list');
    setEditing(null);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, workspace?.id]);

  if (!isOpen || !workspace) return null;

  const onDelete = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    await adminApi.deleteReceiptTemplate(workspace.id, tpl.id);
    await reload();
  };

  const title = `Receipt templates · ${workspace.name}`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      {view === 'list' && (
        <>
          <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
            <button
              className="admin-btn admin-btn-primary"
              onClick={() => setView('create')}
            >
              <Upload size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Upload template
            </button>
          </div>

          {templates === null && <div style={{ color: 'var(--text-muted)' }}>Loading…</div>}

          {templates !== null && templates.length === 0 && (
            <div style={{
              padding: 16,
              background: 'var(--warning-bg)',
              border: `1px solid ${COLORS.warningBorder}`,
              borderRadius: 6,
              fontSize: 13,
              color: 'var(--warning-text)',
            }}>
              No receipt templates yet for this workspace.
            </div>
          )}

          {templates && templates.length > 0 && (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Created</th>
                  <th style={{ width: 90 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((tpl) => (
                  <tr key={tpl.id}>
                    <td><strong>{tpl.name}</strong></td>
                    <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>{formatDate(tpl.created_at)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          onClick={() => { setEditing(tpl); setView('edit'); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                          title="Edit"
                        >
                          <Pencil size={16} color={COLORS.textMuted} />
                        </button>
                        <button
                          onClick={() => onDelete(tpl)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                          title="Delete"
                        >
                          <Trash2 size={16} color={COLORS.danger} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {view === 'create' && (
        <TemplateForm
          workspaceId={workspace.id}
          mode="create"
          template={null}
          onDone={async () => { await reload(); setView('list'); }}
        />
      )}

      {view === 'edit' && editing && (
        <TemplateForm
          workspaceId={workspace.id}
          mode="edit"
          template={editing}
          onDone={async () => { await reload(); setView('list'); setEditing(null); }}
        />
      )}
    </Modal>
  );
}
